import React from "react";

import Panel from "ui/Panel";
import { StatusKPITiles } from "../KPITiles";

import {
  GetIndexAdvisorStatusQuery,
  useGetIndexAdvisorStatusQuery,
} from "./gql/Query.generated";
type QueriesWithErrorsType =
  GetIndexAdvisorStatusQuery["getIndexAdvisorStatus"]["queriesWithErrors"];
type TableStatsType =
  GetIndexAdvisorStatusQuery["getIndexAdvisorStatus"]["tableStats"];

import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import Loading from "ui/Loading";
import Grid, { NoDataGridContainer } from "ui/Grid";
import { formatPercent, formatTimestampLong } from "utils/format";
import { useRoutes } from "utils/routes";
import { Link } from "react-router-dom";
import { useQueryStringSearch } from "ui/QueryStringSearch";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { makeFilter } from "utils/filter";

const Status: React.FunctionComponent<{
  databaseId: string;
}> = ({ databaseId }) => {
  const { data, loading, error } = useGetIndexAdvisorStatusQuery({
    variables: {
      databaseId,
    },
  });
  const stats = data?.getIndexAdvisorStatus;
  return (
    <>
      <StatusKPITiles
        loading={loading || !!error}
        analyzedCount={stats?.analyzedCount}
        processedTables={stats?.processedTables}
        totalTables={stats?.totalTables}
        lastProcessed={stats?.lastProcessed}
        analyzedQueriesPercentage={stats?.analyzedQueriesPercentage}
      />
      <TablesProcessedGrid
        loading={loading}
        error={error}
        data={stats?.tableStats}
        databaseId={databaseId}
      />
      <QueriesWithErrorsGrid
        loading={loading}
        error={error}
        data={stats?.queriesWithErrors}
        databaseId={databaseId}
      />
    </>
  );
};

const QueriesWithErrorsGrid: React.FunctionComponent<{
  loading: boolean;
  error: Error;
  data: QueriesWithErrorsType;
  databaseId: string;
}> = ({ loading, error, data, databaseId }) => {
  const { databaseQuery } = useRoutes();
  const [searchTerm, SearchInput] = useQueryStringSearch("qe");
  if (loading || error) {
    return (
      <Panel title="Queries With Errors">
        <Loading error={!!error} />
      </Panel>
    );
  }
  if (data.length === 0) {
    return (
      <Panel title="Queries With Errors">
        <NoDataGridContainer>No errors found</NoDataGridContainer>
      </Panel>
    );
  }

  const filteredData = data.filter((dat) => {
    // Not using makeFilter as it needs to access a nested value
    if (searchTerm === "") {
      return true;
    }
    return (
      dat["error"].toString().toLowerCase().includes(searchTerm) ||
      dat["tableNames"].toString().toLowerCase().includes(searchTerm)
    );
  });
  return (
    <Panel
      title={`Queries With Errors (${filteredData.length})`}
      secondaryTitle={<SearchInput />}
    >
      <Grid
        data={filteredData}
        defaultSortBy="pctOfTotal"
        columns={[
          {
            field: "truncatedQuery",
            header: "Query",
            width: "1fr",
            className: "font-[monospace]",
            renderer: function QueryCell({ fieldData, rowData }) {
              return (
                <Link
                  to={databaseQuery(databaseId, rowData.id.toString())}
                  title={fieldData}
                >
                  {fieldData}
                </Link>
              );
            },
          },
          {
            field: "error",
            header: "Error",
            width: "400px",
            renderer: function ErrorCell({ fieldData }) {
              return <span title={fieldData}>{fieldData}</span>;
            },
          },
          {
            field: "pctOfTotal",
            header: "% of all runtime",
            width: "150px",
            style: "number",
            renderer: ({ fieldData }) => formatPercent(fieldData / 100),
            defaultSortOrder: "DESC",
          },
        ]}
        pageSize={10}
      />
    </Panel>
  );
};

const TablesProcessedGrid: React.FunctionComponent<{
  loading: boolean;
  error: Error;
  data: TableStatsType;
  databaseId: string;
}> = ({ loading, error, data, databaseId }) => {
  const { databaseTableIndexes } = useRoutes();
  const [searchTerm, SearchInput] = useQueryStringSearch("qt");
  if (loading || error) {
    return (
      <Panel title="Index Advisor Status per Table">
        <Loading error={!!error} />
      </Panel>
    );
  }

  const filteredData = data.filter(makeFilter(searchTerm, "tableName"));
  return (
    <Panel
      title={`Index Advisor Status per Table (${filteredData.length})`}
      secondaryTitle={<SearchInput />}
    >
      <Grid
        data={filteredData}
        defaultSortBy="insightsCount"
        columns={[
          {
            field: "tableName",
            header: "Table",
            width: "1fr",
            renderer: function TableNameCell({ fieldData, rowData }) {
              return (
                <Link
                  to={databaseTableIndexes(databaseId, rowData.id.toString())}
                >
                  {fieldData}
                </Link>
              );
            },
          },
          {
            field: "scansCount",
            header: "Scans on table",
            width: "160px",
            style: "number",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            tip: "The number of distinct scans from the query workload on this table.",
          },
          {
            field: "insightsCount",
            header: "Insights",
            width: "160px",
            style: "number",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            tip: "Total insights count of the table. This includes both Missing Index and Unused Index insights.",
          },
          {
            field: "lastRun",
            header: "Last Run",
            width: "180px",
            renderer: function LastRunCell({ fieldData, rowData }) {
              return (
                <>
                  {fieldData
                    ? formatTimestampLong(moment(fieldData * 1000))
                    : "n/a"}
                  {rowData.failed && (
                    <span title="Last run failed" className="ml-2">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </span>
                  )}
                </>
              );
            },
            tip: "The last time the Indexing Engine processed this table to find insights.",
          },
        ]}
        pageSize={10}
      />
    </Panel>
  );
};

export default Status;
